/**
 * UserMenu.jsx — shared login/profile control for trip-planner pages
 *
 * Standardized to match the EXACT dropdown styling/behavior already used in
 * the site's real Navbar.js (avatar with initial-letter fallback, headlessui
 * Menu, "My Bookings" Link, "Logout" with the private-path redirect + reload
 * pattern). Visually and behaviorally identical to what users already see
 * on every other page — this isn't a new design, it's the same one reused.
 *
 * Context: src/context/AuthContext.js
 *   userProfile.code: 1 = loading, 2 = logged in, 3 = logged out
 *   userProfile.displayName / userProfile.photoURL / userProfile.email
 *   openLoginModal() — opens the app's global <LoginModal/>
 *   handleLogout()   — Firebase signOut()
 *
 * Drop this into any page's top-right corner:
 *   <UserMenu />
 */

import { useNavigate, useLocation, Link } from "react-router-dom";
import { Menu } from "@headlessui/react";
import { useAuth } from "../context/AuthContext";
import "../styles/Navbar.css";

export default function UserMenu() {
  const navigate = useNavigate();
  const location = useLocation();
  const { userProfile, openLoginModal, handleLogout: logoutFromContext } = useAuth();

  const handleLogout = async () => {
    try {
      await logoutFromContext();

      // Same pattern as Navbar.js — redirect home only if leaving a private page
      const privatePaths = ["/booking-history", "/booking-detail", "/book-flight", "/booking-flight"];
      const isPrivatePath = privatePaths.some(p => location.pathname.startsWith(p));
      if (isPrivatePath) navigate("/");

      window.location.reload();
    } catch (error) {
      console.error("Logout error:", error);
    }
  };

  // Still resolving auth state — placeholder avoids layout shift.
  if (!userProfile || userProfile.code === 1) {
    return <div style={{ width: 88, height: 36 }} />;
  }

  // Logged out
  if (userProfile.code === 3) {
    return (
      <button className="signin-nav-btn" onClick={() => openLoginModal()}>
        Sign In
      </button>
    );
  }

  // Logged in (code === 2) — avatar + dropdown, identical to Navbar.js
  const hasPhoto = userProfile.photoURL && userProfile.photoURL !== "/profileDefault.png";

  return (
    <div className="user-profile-wrapper" style={{ position: "relative" }}>
      <Menu as="div" className="user-dropdown">
        <Menu.Button style={{
          background: "transparent",
          border: "none",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          gap: "8px",
          padding: "4px 8px",
          borderRadius: "8px",
        }}>
          <span style={{ fontSize: "14px", fontWeight: 500, color: "#374151", textTransform: "capitalize" }}>
            {userProfile.displayName}
          </span>
          {hasPhoto ? (
            <img
              src={userProfile.photoURL}
              alt="Profile"
              style={{ width: 32, height: 32, borderRadius: "50%", objectFit: "cover" }}
              onError={(e) => {
                e.target.style.display = "none";
                e.target.parentElement.querySelector(".avatar-fallback").style.display = "flex";
              }}
            />
          ) : null}
          <div
            className="avatar-fallback"
            style={{
              width: 32, height: 32, borderRadius: "50%",
              backgroundColor: "#FF5B15", color: "white",
              display: hasPhoto ? "none" : "flex",
              alignItems: "center", justifyContent: "center",
              fontSize: 14, fontWeight: 600, textTransform: "uppercase",
            }}
          >
            {userProfile.email ? (
              userProfile.displayName?.charAt(0) || "U"
            ) : (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                <circle cx="12" cy="7" r="4" />
              </svg>
            )}
          </div>
        </Menu.Button>
        <Menu.Items style={{
          position: "absolute", right: 0, marginTop: "4px", width: "180px",
          backgroundColor: "white", border: "1px solid #E5E7EB", borderRadius: "8px",
          boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)", padding: "4px 0", zIndex: 1001,
        }}>
          <Menu.Item>
            {({ active }) => (
              <Link
                to="/booking-history"
                style={{
                  display: "block", padding: "8px 16px", fontSize: "14px",
                  color: "#374151", textDecoration: "none",
                  backgroundColor: active ? "#F9FAFB" : "transparent",
                }}
              >
                My Bookings
              </Link>
            )}
          </Menu.Item>
          <Menu.Item>
            {({ active }) => (
              <button
                onClick={handleLogout}
                style={{
                  width: "100%", textAlign: "left", padding: "8px 16px", fontSize: "14px",
                  color: "#EF4444", border: "none",
                  backgroundColor: active ? "#F9FAFB" : "transparent", cursor: "pointer",
                }}
              >
                Logout
              </button>
            )}
          </Menu.Item>
        </Menu.Items>
      </Menu>
    </div>
  );
}
